/**
 * Style-level Sample requests — MVP scope (per dl-61559's approved plan): a
 * read-only, single-style SKU kanban board over the 4 hardcoded built-in
 * pipeline stages (org-level admin CRUD of custom stages is out of the
 * epic's scope). Cards are non-interactive — the rich review drawer belongs
 * to dl-61571.
 *
 * Values live on the StyleRow (`samplePipelineConfig`, `sampleRequestSkus`),
 * seeded deterministically so they're stable across reloads. Seeding reads
 * the style's own already-generated `colors`/`sizes`/`season` (no new faker
 * draws), so it doesn't perturb the shared faker stream other styles-v2 mock
 * data depends on staying byte-identical (see `deriveCustomFields`'s note).
 */
import type { StyleColor, SampleStage, SampleRequestStatus, SampleRequestSku } from '../types';

export const ALL_STAGES: SampleStage[] = ['proto', 'fit', 'sales', 'production'];

export const SAMPLE_STAGE_LABELS: Record<SampleStage, string> = {
  proto: 'Proto',
  fit: 'Fit',
  sales: 'Sales',
  production: 'Production',
};

const SAMPLE_STATUSES: SampleRequestStatus[] = ['pending', 'in_progress', 'approved', 'rejected'];

export const SAMPLE_STATUS_LABELS: Record<SampleRequestStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  approved: 'Approved',
  rejected: 'Rejected',
};

const BASE_DATE = new Date('2026-08-01T00:00:00.000Z').getTime();
const DAY_MS = 86400000;

const hash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export type SamplePipelineSeed = {
  pipelineConfig: SampleStage[] | null;
  sampleRequestSkus: SampleRequestSku[];
};

/**
 * Deterministic sample-pipeline seed for a style (pure hash of styleNumber +
 * the style's own already-generated colors/sizes/season — no faker draws).
 * One in five styles seeds genuinely unconfigured ("Set up sample stages");
 * of the configured remainder, one in four seeds configured-but-empty ("No
 * sample requests yet"), for real coverage of both empty states.
 */
export const deriveSamplePipeline = (
  styleNumber: string,
  colors: StyleColor[],
  sizes: string[],
  season: string
): SamplePipelineSeed => {
  const configHash = hash(`${styleNumber}:sample-config`);
  if (configHash % 5 === 0) return { pipelineConfig: null, sampleRequestSkus: [] };

  const stageCount = (configHash % 3) + 2; // 2..4 of the 4 built-ins
  const pipelineConfig = ALL_STAGES.slice(0, stageCount);

  const populatedHash = hash(`${styleNumber}:sample-populated`);
  if (populatedHash % 4 === 0) return { pipelineConfig, sampleRequestSkus: [] };

  const requestCount = (populatedHash % 2) + 1; // 1..2 requests
  const sampleRequestSkus: SampleRequestSku[] = [];

  for (let r = 0; r < requestCount; r++) {
    const requestId = `${styleNumber}-sr-${r}`;
    const requestName = `Sample request ${r + 1}`;
    const reqHash = hash(`${requestId}`);
    const pickedColors = colors.filter((_, i) => (reqHash >> i) & 1);
    const usedColors = pickedColors.length ? pickedColors : [colors[0]];

    for (const color of usedColors) {
      for (const size of sizes) {
        const h = hash(`${requestId}:${color.id}:${size}`);
        if (h % 3 === 0) continue; // sparser board — not every colour×size combo has a card

        sampleRequestSkus.push({
          id: `${requestId}-${color.id}-${size}`,
          requestId,
          requestName,
          colorId: color.id,
          colorName: color.name,
          size,
          quantity: (h % 3) + 1,
          season,
          stage: pipelineConfig[h % pipelineConfig.length],
          status: SAMPLE_STATUSES[h % SAMPLE_STATUSES.length],
          dueDate: new Date(BASE_DATE + (h % 60) * DAY_MS).toISOString().split('T')[0],
        });
      }
    }
  }

  return { pipelineConfig, sampleRequestSkus };
};
